// Floor API service for backend integration
import { API_CONFIG, API_ENDPOINTS } from '../constants/config';

// Backend DTOs based on swagger documentation
export interface FloorDto {
  id: number;
  name: string;
}

export interface AreaDto {
  id: number;
  name: string;
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  type: string;
  color: string;
  floorId: number;
}

export interface DividerDto {
  id: number;
  x1position: number;
  y1position: number;
  x2position: number;
  y2position: number;
  thickness: number;
  type: string;
  color: string;
  rotation: number;
  floorId: number;
}

export interface RestaurantTableDto {
  id: number;
  name: string;
  seats: number;
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE';
  location: string;
  description: string;
  shape: string;
  color: string;
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  rotation: number;
  floorId: number;
  createdAt?: string;
  updatedAt?: string;
}

// Backend API DTOs - exact match from backend code analysis
export interface RestaurantTableRequestDto {
  id?: number;
  name: string;
  seats: number;
  status: 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE';
  location: string;
  description: string;
  shape: string;
  color: string;
  xposition: number;
  yposition: number;
  floorId: number;
  width: number;
  height: number;
  rotation: number;
}

export interface FloorResponseDto {
  id: number;
  name: string;
  tables: RestaurantTableRequestDto[];
  areas: AreaDto[];
  dividers: DividerDto[];
}

export interface PaginatedResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
  };
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

class FloorApiService {
  private baseURL: string;

  constructor() {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://admin.grandpittu.lk');
    this.baseURL = `${baseUrl}${API_CONFIG.SLUG}`;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };

    const url = `${this.baseURL}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`Floor API request failed: ${response.status} ${errorText}`);
      }

      // Handle different response types
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const data = await response.json();
        return data;
      } else {
        const text = await response.text();
        return text as unknown as T;
      }
    } catch (error: unknown) {
      clearTimeout(timeoutId);

      // if (error instanceof Error && error.name === 'AbortError') {
      //   console.warn(`Floor API Request timeout`);
      // }

      // Retry logic
      if (retryCount < API_CONFIG.RETRY_ATTEMPTS &&
        error instanceof Error &&
        (error.name === 'AbortError' || error.message.includes('fetch'))) {
        const delay = API_CONFIG.EXPONENTIAL_BACKOFF
          ? API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount)
          : API_CONFIG.RETRY_DELAY;

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }
  }

  // Get all floors (basic info only)
  async getAllFloors(): Promise<FloorDto[]> {
    return this.makeRequest<FloorDto[]>(API_ENDPOINTS.FLOOR.GET_ALL);
  }

  // Get floor details with tables, areas, and dividers by ID
  async getFloorDetailsById(id: number): Promise<FloorResponseDto> {
    return this.makeRequest<FloorResponseDto>(API_ENDPOINTS.FLOOR.GET_DETAILS_BY_ID(id));
  }

  // Get all floor details with tables, areas, and dividers (paginated)
  async getAllFloorDetailsWithTablesAndAreas(page = 0, size = 10): Promise<PaginatedResponse<FloorResponseDto>> {
    return this.makeRequest<PaginatedResponse<FloorResponseDto>>(
      `${API_ENDPOINTS.FLOOR.GET_ALL_DETAILS}?page=${page}&size=${size}`
    );
  }

  // Create floor
  async createFloor(floorData: { name: string }): Promise<string> {
    return this.makeRequest<string>(API_ENDPOINTS.FLOOR.BASE, {
      method: 'POST',
      body: JSON.stringify(floorData),
    });
  }

  // Update floor
  async updateFloor(id: number, name: string): Promise<FloorDto> {
    return this.makeRequest<FloorDto>(`${API_ENDPOINTS.FLOOR.BY_ID(id)}?name=${encodeURIComponent(name)}`, {
      method: 'PUT',
    });
  }

  // Delete floor
  async deleteFloor(id: number): Promise<string> {
    return this.makeRequest<string>(API_ENDPOINTS.FLOOR.BY_ID(id), {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const floorApi = new FloorApiService();
export default floorApi;